import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "../../services/authService";
import { useUserStore } from "../../store/useUserStore";
import toast from "react-hot-toast";
import { queryClient } from "../../config/queryClient";
import { setLogoutInProgress } from "../../services/api";

const getAskFinuOnboardingCompleted = (user) =>
  user?.ask_finu_onboarding_completed ||
  user?.askFinuOnboardingCompleted ||
  false;

// Async thunks
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // Clear a stale marker from a previous interrupted logout before login.
      setLogoutInProgress(false);
      // The users-me query is shared by all accounts. Remove the previous
      // account's cached profile before authenticating a new account.
      queryClient.removeQueries({ queryKey: ["users-me"] });
      const response = await authService.login(email, password);
      toast.success("Login successful!");
      return response;
    } catch (error) {
      // Display the actual error message from the backend
      toast.error(error.message || "Login failed");
      return rejectWithValue(error.message);
    }
  },
);

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      toast.error(error.message || "Registration failed");
      return rejectWithValue(error.message);
    }
  },
);

export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      setLogoutInProgress(false);
      const result = await authService.loginWithGoogle();
      if (result.success) {
        // Get the stored token and user data
        const token = localStorage.getItem("token");
        const userData = JSON.parse(localStorage.getItem("user") || "{}");

        return {
          user: userData,
          token: token,
          success: true,
        };
      } else {
        throw new Error("OAuth authentication failed");
      }
    } catch (error) {
      toast.error(error.message || "Google OAuth failed");
      return rejectWithValue(error.message);
    }
  },
);

export const authenticateWeb3 = createAsyncThunk(
  "auth/authenticateWeb3",
  async ({ address, jwt }, { rejectWithValue }) => {
    try {
      setLogoutInProgress(false);
      const response = await authService.authenticateWeb3(address, jwt);
      return response;
    } catch (error) {
      toast.error(error.message || "Web3 authentication failed");
      return rejectWithValue(error.message);
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await authService.logout();

      // Do not let an in-flight/cached request from the previous account
      // hydrate the next session. This query cache is process-wide and its
      // key does not contain a user id.
      await queryClient.cancelQueries();
      queryClient.clear();

      // Reset all app state
      dispatch({ type: "RESET_APP" });

      // Modal cleanup runs as part of the reset/unmount and can otherwise
      // restore its old `overflow: hidden` value after this thunk finishes.
      // Restore once now and once after React has flushed those cleanups.
      const restoreDocumentScroll = () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.documentElement.style.overflow = "";
      };
      restoreDocumentScroll();
      window.setTimeout(restoreDocumentScroll, 0);

      // Keep the guard alive briefly for requests that were already past
      // cancellation when logout started. A new login clears it first.
      window.setTimeout(() => {
        setLogoutInProgress(false);
      }, 5000);

      toast.success("Logged out successfully");
      return null;
    } catch (error) {
      setLogoutInProgress(false);
      toast.error("Logout failed");
      return rejectWithValue(error.message);
    }
  },
);

export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.checkAuth();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async (token, { rejectWithValue }) => {
    try {
      const response = await authService.verifyEmail(token);
      return response;
    } catch (error) {
      toast.error(error.message || "Email verification failed");
      return rejectWithValue(error.message);
    }
  },
);

export const resendVerification = createAsyncThunk(
  "auth/resendVerification",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.resendVerification(email);
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to resend verification");
      return rejectWithValue(error.message);
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to send password reset");
      return rejectWithValue(error.message);
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(token, newPassword);
      return response;
    } catch (error) {
      toast.error(error.message || "Password reset failed");
      return rejectWithValue(error.message);
    }
  },
);

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(profileData);
      return response;
    } catch (error) {
      console.error("Redux: Profile update error:", error);
      toast.error(error.message || "Failed to update profile");
      return rejectWithValue(error.message);
    }
  },
);

export const completeOnboarding = createAsyncThunk(
  "auth/completeOnboarding",
  async (onboardingData, { rejectWithValue }) => {
    try {
      const response = await authService.completeOnboarding(onboardingData);
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to complete onboarding");
      return rejectWithValue(error.message);
    }
  },
);

// Initialize state based on stored data
const getInitialState = () => {
  const token = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");

  // If we have a token, assume authenticated until proven otherwise
  const isAuthenticated = Boolean(token);

  let user = null;
  let onboardingCompleted = false;
  let onboardingSkipped = false;
  let askFinuOnboardingCompleted = false;

  if (storedUser) {
    try {
      user = JSON.parse(storedUser);
      // Check both possible field names for backward compatibility
      onboardingCompleted =
        user?.onboarding_completed || user?.onboardingCompleted || false;
      onboardingSkipped =
        user?.onboarding_skipped || user?.onboardingSkipped || false;
      askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(user);
    } catch (e) {
      console.warn("Failed to parse stored user data:", e);
    }
  }

  return {
    user,
    token,
    isAuthenticated,
    isLoading: false,
    error: null,
    onboardingCompleted,
    onboardingSkipped,
    askFinuOnboardingCompleted,
  };
};

const initialState = getInitialState();

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUserFromMe: (state, action) => {
      const user = action.payload || null;
      // Skip when unchanged: otherwise every /users/me response replaces the
      // `user` reference (re-running all user-keyed effects across the shell)
      // and rewrites localStorage — the main cause of the render/fetch storm.
      // Compare with the volatile `balance` field stripped out — its
      // `spendingPower` changes on essentially every call and would otherwise
      // defeat this guard, churning the user object indefinitely.
      const stableKey = (u) => {
        if (!u) return "null";
        const { balance, ...rest } = u;
        return JSON.stringify(rest);
      };
      if (stableKey(user) === stableKey(state.user)) {
        const currentBalance = state.user?.balance ?? null;
        const incomingBalance = user?.balance ?? null;
        const balanceChanged =
          JSON.stringify(currentBalance) !== JSON.stringify(incomingBalance);

        // Keep the anti-churn guard for stable profile data, but still allow
        // live balance fields (e.g. spendingPower) to refresh from /users/me.
        if (balanceChanged && state.user) {
          state.user.balance = incomingBalance;
          localStorage.setItem("user", JSON.stringify(state.user));
        }
        return;
      }
      state.user = user;
      state.isAuthenticated = Boolean(
        state.token || localStorage.getItem("token"),
      );
      state.onboardingCompleted =
        user?.onboarding_completed || user?.onboardingCompleted || false;
      state.onboardingSkipped =
        user?.onboarding_skipped || user?.onboardingSkipped || false;
      state.askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(user);

      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }
    },
    clearAuthLocal: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.onboardingCompleted = false;
      state.onboardingSkipped = false;
      state.askFinuOnboardingCompleted = false;
      state.error = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    },
    setOnboardingCompleted: (state, action) => {
      state.onboardingCompleted = true;
      state.onboardingSkipped = false;
      if (state.user) {
        state.user.onboarding_completed = true;
        state.user.onboarding_skipped = false;
        // Update localStorage with the new user data
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateUserProfileSync: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    updateSubscription: (state, action) => {
      if (state.user) {
        state.user.subscription = action.payload;
      }
    },
    handleOAuthCallback: (state, action) => {
      const { user, token, success } = action.payload;
      if (success) {
        state.user = user;
        state.token = token;
        state.isAuthenticated = true;
        state.onboardingCompleted = user?.onboarding_completed || false;
        state.onboardingSkipped = user?.onboarding_skipped || false;
        state.askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(user);
      }
    },
    handleEmailVerificationCallback: (state, action) => {
      const { user, token, success } = action.payload;
      if (success) {
        state.user = user;
        state.token = token;
        state.isAuthenticated = true;
        state.onboardingCompleted = user?.onboarding_completed || false;
        state.onboardingSkipped = user?.onboarding_skipped || false;
        state.askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(user);
      }
    },
    skipOnboarding: (state) => {
      state.onboardingCompleted = false;
      state.onboardingSkipped = true;
      if (state.user) {
        state.user.onboarding_completed = false;
        state.user.onboarding_skipped = true;
        // Update localStorage with the new user data
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    setAskFinuOnboardingCompleted: (state, action) => {
      state.askFinuOnboardingCompleted = true;
      if (state.user) {
        state.user.ask_finu_onboarding_completed = true;
        state.user.askFinuOnboardingCompleted = true;
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.onboardingCompleted =
          action.payload.user?.onboarding_completed || false;
        state.onboardingSkipped =
          action.payload.user?.onboarding_skipped || false;
        state.askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(
          action.payload.user,
        );
        // Ensure localStorage is updated
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem("user", JSON.stringify(action.payload.user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = false; // User needs to verify email first
        state.onboardingCompleted = false;
        state.onboardingSkipped = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Google OAuth
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.onboardingCompleted =
          action.payload.user?.onboarding_completed || false;
        state.onboardingSkipped =
          action.payload.user?.onboarding_skipped || false;
        state.askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(
          action.payload.user,
        );
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Web3 Authentication
      .addCase(authenticateWeb3.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authenticateWeb3.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.onboardingCompleted =
          action.payload.user?.onboarding_completed || false;
        state.onboardingSkipped =
          action.payload.user?.onboarding_skipped || false;
        state.askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(
          action.payload.user,
        );
      })
      .addCase(authenticateWeb3.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.onboardingCompleted = false;
        state.onboardingSkipped = false;
        state.askFinuOnboardingCompleted = false;
        state.error = null;

        // Also clear mirrored auth state in Zustand store (Crossmint / wallet login)
        try {
          useUserStore.getState().clearAuth();
        } catch (e) {
          // Ignore if store is not initialised yet
          console.warn("Failed to clear user store auth state on logout:", e);
        }
      })
      // Check Auth Status
      .addCase(checkAuthStatus.pending, (state) => {
        // Only set loading if we don't already have user data
        if (!state.user) {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.onboardingCompleted =
            action.payload.user?.onboarding_completed || false;
          state.onboardingSkipped =
            action.payload.user?.onboarding_skipped || false;
          state.askFinuOnboardingCompleted = getAskFinuOnboardingCompleted(
            action.payload.user,
          );
          // Ensure localStorage is updated with fresh data
          localStorage.setItem("token", action.payload.token);
          localStorage.setItem("user", JSON.stringify(action.payload.user));
        }
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.onboardingCompleted = false;
        state.onboardingSkipped = false;
        state.askFinuOnboardingCompleted = false;
        // Clear invalid token from localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      })
      // Email Verification
      .addCase(verifyEmail.fulfilled, (state, action) => {
        // Handle email verification success
        state.isAuthenticated = true;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Get Current User
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update Profile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        if (state.user) {
          state.user = { ...state.user, ...action.payload };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Complete Onboarding
      .addCase(completeOnboarding.fulfilled, (state, action) => {
        state.onboardingCompleted = true;
        state.onboardingSkipped = false;
        if (state.user) {
          state.user = { ...state.user, ...action.payload };
          state.user.onboarding_completed = true;
          state.user.onboarding_skipped = false;
          // Update localStorage with the new user data
          localStorage.setItem("user", JSON.stringify(state.user));
        }
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setUserFromMe,
  clearAuthLocal,
  setOnboardingCompleted,
  updateUserProfileSync,
  updateSubscription,
  handleOAuthCallback,
  handleEmailVerificationCallback,
  skipOnboarding,
  setAskFinuOnboardingCompleted,
} = authSlice.actions;
export default authSlice.reducer;
